import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/apiClient";
import type {
  ReceivedBatchHistoryRow,
  Supplier,
  TypedMoneyBasis,
  UpdateReceivedBatchPayload,
} from "../api/types";
import { money } from "../i18n/intl";
import {
  IVA_TAKEN_OUT_HINT,
  ivaBasisOptions,
  PICK_IVA_BASIS,
  purchaseIvaNote,
} from "../utils/purchaseMoneyText";
import { lotCostSourceLabel } from "../utils/lotCostSource";
import { basisAfterEdit } from "../utils/typedMoneyBasis";
import { Modal } from "./Modal";
import { SegmentedControl } from "./ui/SegmentedControl";

function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatSourceLabel(source: ReceivedBatchHistoryRow["source"]): string {
  switch (source) {
    case "delivery":
      return "Delivery";
    case "single_use":
      return "Receive";
    case "flower":
      return "Flowers";
    case "adjustment":
      return "Adjustment";
    default:
      return "Other";
  }
}

function formatWhen(iso: string): string {
  const d = Date.parse(iso);
  if (!Number.isFinite(d)) return "—";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(d));
}

const PAGE_SIZE = 50;

export type ReceiveBatchHistoryModalProps = {
  open: boolean;
  onClose: () => void;
  locationId: string;
  locationName: string;
  onSaved: () => void;
};

export function ReceiveBatchHistoryModal({
  open,
  onClose,
  locationId,
  locationName,
  onSaved,
}: ReceiveBatchHistoryModalProps) {
  const [items, setItems] = useState<ReceivedBatchHistoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<ReceivedBatchHistoryRow | null>(null);

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [editReason, setEditReason] = useState("");
  const [editInvoiced, setEditInvoiced] = useState("");
  const [editUsable, setEditUsable] = useState("");
  const [editRemaining, setEditRemaining] = useState("");
  const [editInvoiceTotal, setEditInvoiceTotal] = useState("");
  /** G-121 S3: which side of IVA the total box is on (opens as the server says). */
  const [editMoneyBasis, setEditMoneyBasis] = useState<TypedMoneyBasis | null>(null);
  const [editInvoiceNumber, setEditInvoiceNumber] = useState("");
  const [editSupplierId, setEditSupplierId] = useState<string>("");
  const [editPurchasedAt, setEditPurchasedAt] = useState("");
  const [editReceivedAt, setEditReceivedAt] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSearchInput("");
    setDebouncedQ("");
    setOffset(0);
    setItems([]);
    setEditing(null);
    setError(null);
  }, [open]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    if (!open || !locationId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.listReceivedBatches({
        locationId,
        q: debouncedQ || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setItems((prev) => (offset === 0 ? res.items : [...prev, ...res.items]));
      setTotal(res.total);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not load batch history");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [open, locationId, debouncedQ, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!open) return;
    setOffset(0);
  }, [open, debouncedQ]);

  useEffect(() => {
    if (!open || !editing) return;
    let cancelled = false;
    void api
      .listSuppliers(false)
      .then((list) => {
        if (!cancelled) setSuppliers(list);
      })
      .catch(() => {
        if (!cancelled) setSuppliers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, editing]);

  const openEdit = (row: ReceivedBatchHistoryRow) => {
    setEditing(row);
    setEditReason("");
    setEditError(null);
    setEditInvoiced(row.invoicedQty);
    setEditUsable(row.usableQty);
    setEditRemaining(row.remainingQty);
    // G-121 S3: money typed with IVA reopens as its gross on "Includes";
    // everything else as its before-IVA total. The server decides which.
    // WO-B: a lot with no price opens empty — "no money given", not €0.
    setEditInvoiceTotal(row.correctionPrefill.totalEur ?? "");
    setEditMoneyBasis(row.correctionPrefill.moneyBasis);
    setEditInvoiceNumber(row.invoiceNumber ?? "");
    setEditSupplierId(row.supplier?.id ?? "");
    setEditPurchasedAt(toDatetimeLocalValue(row.purchasedAt));
    setEditReceivedAt(toDatetimeLocalValue(row.receivedAt));
  };

  const closeEdit = () => {
    setEditing(null);
    setEditError(null);
  };

  const submitEdit = async () => {
    if (!editing) return;
    const r = editReason.trim();
    if (r.length < 3) {
      setEditError("Reason must be at least 3 characters.");
      return;
    }
    let invoiced: number;
    let received: number;
    let usable: number;

    if (editing.item.isPerishable) {
      invoiced = parseFloat(editInvoiced);
      usable = parseFloat(editUsable);
      // A delivery batch that came in short keeps what arrived (G-121 S2);
      // the server holds the same rule.
      received =
        editing.receivedQty !== editing.invoicedQty ? Number(editing.receivedQty) : invoiced;
      if (!Number.isFinite(invoiced) || invoiced <= 0) {
        setEditError("Enter a valid invoiced quantity.");
        return;
      }
      if (!Number.isFinite(usable) || usable <= 0) {
        setEditError("Enter a valid usable quantity.");
        return;
      }
    } else {
      usable = parseFloat(editUsable);
      if (!Number.isFinite(usable) || usable <= 0) {
        setEditError("Enter a valid quantity.");
        return;
      }
      invoiced = usable;
      received = usable;
    }

    const remaining = parseFloat(editRemaining);
    if (!Number.isFinite(remaining) || remaining < 0) {
      setEditError("Enter a valid remaining quantity.");
      return;
    }
    // WO-B (OD-198): a lot with no price may stay without one — an empty box
    // sends no money and keeps its estimated (or unknown) cost.
    const leaveUnpriced = editing.costSource !== "PRICED" && editInvoiceTotal.trim() === "";
    const invTotal = parseFloat(editInvoiceTotal);
    if (!leaveUnpriced) {
      if (!Number.isFinite(invTotal) || invTotal < 0) {
        setEditError("Enter a valid invoice total (EUR).");
        return;
      }
      if (editMoneyBasis === null) {
        setEditError(PICK_IVA_BASIS);
        return;
      }
    }

    const body: UpdateReceivedBatchPayload = {
      reason: r,
      invoicedQty: invoiced,
      receivedQty: received,
      usableQty: usable,
      remainingQty: remaining,
      ...(leaveUnpriced ? {} : { invoiceTotalEur: invTotal, moneyBasis: editMoneyBasis ?? undefined }),
      invoiceNumber: editInvoiceNumber.trim() || null,
      purchasedAt: new Date(editPurchasedAt).toISOString(),
      receivedAt: new Date(editReceivedAt).toISOString(),
    };
    if (editSupplierId) {
      body.supplierId = editSupplierId;
    } else {
      body.supplierId = null;
    }

    setEditSubmitting(true);
    setEditError(null);
    try {
      await api.updateReceivedBatch(editing.id, body);
      onSaved();
      closeEdit();
      await load();
    } catch (e: unknown) {
      setEditError(e instanceof Error ? e.message : "Could not save changes.");
    } finally {
      setEditSubmitting(false);
    }
  };

  const hasMore = offset + items.length < total;

  const title = useMemo(
    () => `Receive history · ${locationName}`,
    [locationName],
  );

  if (!open) return null;

  if (editing) {
    const perishable = editing.item.isPerishable;
    return (
      <Modal title={`Edit batch — ${editing.item.name}`} onClose={closeEdit}>
        <p className="muted" style={{ marginTop: 0, fontSize: "0.875rem", lineHeight: 1.45 }}>
          Editing changes on-hand stock when <strong>remaining</strong> changes. Corrections are
          audit-logged. Updating invoice total or usable quantity changes FIFO unit cost for future
          sales from this batch.
        </p>
        {editError ? (
          <div className="alert error" role="alert">
            {editError}
          </div>
        ) : null}
        <div className="field">
          <label htmlFor="batch-edit-reason">Reason for change (audit) *</label>
          <textarea
            id="batch-edit-reason"
            rows={2}
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
            disabled={editSubmitting}
            placeholder="e.g. Invoice re-key, count correction"
          />
        </div>
        {perishable ? (
          <>
            <div className="field">
              <label htmlFor="batch-edit-invoiced">Invoiced qty *</label>
              <input
                id="batch-edit-invoiced"
                type="number"
                min={0.001}
                step="0.001"
                value={editInvoiced}
                onChange={(e) => setEditInvoiced(e.target.value)}
                disabled={editSubmitting}
              />
            </div>
            <div className="field">
              <label htmlFor="batch-edit-usable">Usable qty *</label>
              <input
                id="batch-edit-usable"
                type="number"
                min={0.001}
                step="0.001"
                value={editUsable}
                onChange={(e) => setEditUsable(e.target.value)}
                disabled={editSubmitting}
              />
            </div>
            <p className="muted" style={{ margin: 0, fontSize: "0.875rem" }}>
              Receiving waste (invoiced minus usable) is calculated automatically.
            </p>
          </>
        ) : (
          <div className="field">
            <label htmlFor="batch-edit-qty">Quantity (invoiced / received / usable) *</label>
            <input
              id="batch-edit-qty"
              type="number"
              min={0.001}
              step="0.001"
              value={editUsable}
              onChange={(e) => {
                const v = e.target.value;
                setEditUsable(v);
                setEditInvoiced(v);
              }}
              disabled={editSubmitting}
            />
          </div>
        )}
        <div className="field">
          <label htmlFor="batch-edit-remaining">Remaining in batch *</label>
          <input
            id="batch-edit-remaining"
            type="number"
            min={0}
            step="0.001"
            value={editRemaining}
            onChange={(e) => setEditRemaining(e.target.value)}
            disabled={editSubmitting}
          />
        </div>
        <div className="field">
          <label htmlFor="batch-edit-inv-total">
            Invoice total (EUR){editing.costSource === "PRICED" ? " *" : ""}
          </label>
          <input
            id="batch-edit-inv-total"
            type="number"
            min={0}
            step="0.01"
            value={editInvoiceTotal}
            onChange={(e) => {
              setEditInvoiceTotal(e.target.value);
              setEditMoneyBasis((b) => basisAfterEdit(b, e.target.value));
            }}
            disabled={editSubmitting}
          />
          {editInvoiceTotal.trim() !== "" ? (
            <div className="recv-iva-basis">
              <SegmentedControl
                options={ivaBasisOptions(editing.item.category.vatRatePercent)}
                value={editMoneyBasis}
                onChange={setEditMoneyBasis}
                disabled={editSubmitting}
                ariaLabel="Is this total before IVA or does it include IVA?"
              />
            </div>
          ) : null}
          <p className="field-hint">
            {editing.costSource !== "PRICED" && editInvoiceTotal.trim() === ""
              ? editing.costSource === "UNKNOWN"
                ? "No price was given and the app had none to borrow: this stock's cost is unknown. Type what was paid to price it, or leave it empty."
                : `No price was given; the cost (${money(editing.purchaseUnitCostEur)} a unit) was borrowed from ${
                    editing.costSource === "ESTIMATED_CATALOG" ? "the catalogue" : "the last delivery"
                  }. Type what was paid to price it, or leave it empty.`
              : editing.moneyBasis === "INVOICE"
              ? "This batch came from a supplier invoice; its IVA is on the invoice. A new total changes only its cost before IVA."
              : editMoneyBasis === null
                ? PICK_IVA_BASIS
                : editMoneyBasis === "INCLUDES_IVA"
                  ? IVA_TAKEN_OUT_HINT
                  : "Stored as the cost before IVA."}
          </p>
        </div>
        <div className="field">
          <label htmlFor="batch-edit-inv-no">Invoice number</label>
          <input
            id="batch-edit-inv-no"
            type="text"
            maxLength={64}
            value={editInvoiceNumber}
            onChange={(e) => setEditInvoiceNumber(e.target.value)}
            disabled={editSubmitting}
          />
        </div>
        <div className="field">
          <label htmlFor="batch-edit-supplier">Supplier (optional)</label>
          <select
            id="batch-edit-supplier"
            value={editSupplierId}
            onChange={(e) => setEditSupplierId(e.target.value)}
            disabled={editSubmitting}
          >
            <option value="">None</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="batch-edit-purchased">Purchased at *</label>
          <input
            id="batch-edit-purchased"
            type="datetime-local"
            value={editPurchasedAt}
            onChange={(e) => setEditPurchasedAt(e.target.value)}
            disabled={editSubmitting}
          />
        </div>
        <div className="field">
          <label htmlFor="batch-edit-received">Received at *</label>
          <input
            id="batch-edit-received"
            type="datetime-local"
            value={editReceivedAt}
            onChange={(e) => setEditReceivedAt(e.target.value)}
            disabled={editSubmitting}
          />
        </div>
        <div className="button-row" style={{ marginTop: "var(--space-3)" }}>
          <button type="button" className="button" disabled={editSubmitting} onClick={() => void submitEdit()}>
            {editSubmitting ? "Saving…" : "Save changes"}
          </button>
          <button type="button" className="button plain" disabled={editSubmitting} onClick={closeEdit}>
            Cancel
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={title} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <div className="field">
          <label htmlFor="batch-history-search">Search by item name</label>
          <input
            id="batch-history-search"
            type="search"
            className="inventory-search-input"
            style={{ width: "100%", maxWidth: "min(100%, 420px)" }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Filter batches…"
            autoComplete="off"
          />
        </div>
        {error ? (
          <div className="alert error" role="alert">
            {error}
          </div>
        ) : null}
        {loading ? (
          <p className="muted" style={{ margin: 0 }}>
            Loading…
          </p>
        ) : null}
        {!loading && items.length === 0 ? (
          <div className="empty-state">No batches match this filter.</div>
        ) : null}
        {items.length > 0 ? (
          <>
            <p className="muted" style={{ margin: 0, fontSize: "0.875rem" }}>
              Showing {items.length} of {total} batch{total === 1 ? "" : "es"}
            </p>
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Received</th>
                    <th>Item</th>
                    <th>Zone</th>
                    <th>Code</th>
                    <th>Source</th>
                    <th>Usable / rem.</th>
                    <th>Total €</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => {
                    // G-121 S3: what is known about the figure's IVA; nothing
                    // for history of unknown basis (never guessed).
                    const ivaNote = purchaseIvaNote(row);
                    return (
                      <tr key={row.id}>
                        <td>{formatWhen(row.receivedAt)}</td>
                        <td>{row.item.name}</td>
                        <td>{row.zone.name}</td>
                        <td style={{ fontFamily: "monospace", fontSize: "12px" }}>{row.batchCode}</td>
                        <td>
                          <span className="badge muted">{formatSourceLabel(row.source)}</span>
                          {lotCostSourceLabel(row.costSource) ? (
                            <span className="badge warning batch-history-cost">
                              {lotCostSourceLabel(row.costSource)}
                            </span>
                          ) : null}
                        </td>
                        <td>
                          {row.usableQty} / {row.remainingQty}
                        </td>
                        <td>
                          {row.costSource === "PRICED" ? money(row.invoiceTotalEur) : <span className="muted">no price</span>}
                          {ivaNote ? <small className="muted batch-history-iva">{ivaNote}</small> : null}
                        </td>
                        <td>{row.status}</td>
                        <td>
                          <button type="button" className="button plain" onClick={() => openEdit(row)}>
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {hasMore ? (
              <div className="button-row" style={{ marginTop: 0 }}>
                <button
                  type="button"
                  className="button tinted"
                  disabled={loading}
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                >
                  Load more
                </button>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </Modal>
  );
}
